import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { cn } from '@workspace/ui/lib/utils'
import {
  HubshiftMascot,
  MascotSpeechBubble,
  type MascotEmotion,
  type MascotVariant,
} from '@/features/assistant/components/mascot'

export const Route = createFileRoute('/(public)/dev/mascot')({
  component: MascotWorkbench,
})

const EMOTIONS: MascotEmotion[] = [
  'idle',
  'greeting',
  'listening',
  'thinking',
  'talking',
  'happy',
  'error',
  'sleeping',
  'waking',
  'dancing',
]

const SIZES = [
  { label: 'FAB (48px)', className: 'h-12 w-12' },
  { label: 'Header (36px)', className: 'h-9 w-9' },
  { label: 'Hero (144px)', className: 'h-36 w-36' },
  { label: 'Large (288px)', className: 'h-72 w-72' },
] as const

/**
 * Dev-only workbench for tuning the assistant mascot. A single canvas with
 * emotion/size switchers (multiple simultaneous canvases would burn WebGL
 * contexts). Not part of the product — renders nothing outside `vite dev`.
 */
function MascotWorkbench() {
  const [emotion, setEmotion] = useState<MascotEmotion>('idle')
  const [size, setSize] = useState<(typeof SIZES)[number]>(SIZES[3])
  const [variant, setVariant] = useState<MascotVariant>('robot')

  if (!import.meta.env.DEV) return null

  return (
    <div className="flex min-h-screen flex-col items-center gap-8 bg-neutral-100 p-10">
      <h1 className="text-lg font-semibold">Hubshift mascot workbench</h1>

      <div
        className="flex h-80 w-80 items-center justify-center rounded-xl bg-white shadow-sm"
        data-testid="mascot-stage"
      >
        <HubshiftMascot emotion={emotion} shadow variant={variant} className={size.className} />
      </div>

      <div className="flex justify-center gap-2">
        {(['robot', 'glowbot'] as const).map((v) => (
          <Button
            key={v}
            size="sm"
            variant={v === variant ? 'default' : 'outline'}
            onClick={() => setVariant(v)}
            data-variant={v}
          >
            {v}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {EMOTIONS.map((e) => (
          <Button
            key={e}
            size="sm"
            variant={e === emotion ? 'default' : 'outline'}
            onClick={() => setEmotion(e)}
            data-emotion={e}
          >
            {e}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {SIZES.map((s) => (
          <Button
            key={s.label}
            size="sm"
            variant={s.label === size.label ? 'secondary' : 'ghost'}
            onClick={() => setSize(s)}
            className={cn(s.label === size.label && 'border')}
          >
            {s.label}
          </Button>
        ))}
      </div>

      {/* Closed-state composition: the floating mascot + its speech bubble,
          exactly as the AssistantPopup renders it bottom-right. */}
      <div
        className="flex items-end gap-12 rounded-xl bg-white p-10 shadow-sm"
        data-testid="bubble-stage"
      >
        <div className="flex flex-col items-end gap-3">
          <MascotSpeechBubble variant="text" text="Hi! Need a hand? 👋" />
          <HubshiftMascot emotion="idle" shadow variant={variant} className="h-20 w-20" />
        </div>
        <div className="flex flex-col items-end gap-3">
          <MascotSpeechBubble variant="thinking" />
          <HubshiftMascot emotion="thinking" shadow variant={variant} className="h-20 w-20" />
        </div>
      </div>

      {/* Open-state geometry mock: bubble panel + tail stacked over the mascot,
          mirroring AssistantPopup's open layout (no live chat wiring here). */}
      <div
        className="flex flex-col items-end gap-3 rounded-xl bg-neutral-200 p-10"
        data-testid="panel-stage"
      >
        <div className="bg-background relative flex h-[360px] w-[420px] flex-col overflow-hidden rounded-2xl border shadow-2xl ring-1 ring-black/10">
          <div className="from-primary/15 via-primary/5 flex items-center gap-2 border-b bg-gradient-to-r to-transparent px-4 py-3">
            <span className="text-sm font-semibold">AI Assistant</span>
          </div>
          <div className="flex flex-1 flex-col items-center justify-center p-6">
            <h2 className="mb-1 text-base font-semibold">How can I help?</h2>
            <p className="text-muted-foreground text-center text-sm">
              Ask about the platform, NDIS/SCHADS, or your live data.
            </p>
          </div>
          <span className="bg-background absolute -bottom-1.5 right-9 h-4 w-4 rotate-45 border-b border-r ring-1 ring-black/10" />
        </div>
        <HubshiftMascot emotion="talking" shadow variant={variant} className="h-20 w-20" />
      </div>
    </div>
  )
}
